import { useNavigate } from "react-router-dom";
import { ArrowRight, PencilLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "@/hooks/useTranslation";
import { USER_ROUTES } from "@/routes";
import { groupOf, isStepId } from "@/lib/onboarding/stepMachine";
import type { EventOnboardingSummary } from "@/services/api/events";
import { useRouteLocale } from "@/hooks/useRouteLocale";

interface OnboardingResumePanelProps {
  eventUuid: string;
  onboarding: EventOnboardingSummary;
}

/**
 * JUB-154 §18/§31: a draft whose wizard is still running shows where it
 * stopped and resumes it — never "Create event" again. The editor stays
 * reachable: the wizard helps, it does not hold the customer captive.
 */
export function OnboardingResumePanel({ eventUuid, onboarding }: OnboardingResumePanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locale = useRouteLocale();
  const step = isStepId(onboarding.step) ? onboarding.step : "people";
  const percent = Math.max(0, Math.min(100, onboarding.progress ?? 0));

  return (
    <div className="space-y-3 rounded-xl border border-pink-200 bg-pink-50/60 p-4 dark:border-pink-900/40 dark:bg-pink-950/20" data-testid="onboarding-resume-panel">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-semibold" data-testid="onboarding-resume-progress">
          {t("onboarding.resume.inProgress", { percent })}
        </span>
        <span className="text-muted-foreground">
          {t("onboarding.resume.stage", { group: t(`onboarding.groups.${groupOf(step)}`) })}
        </span>
      </div>
      <Progress value={percent} className="h-2" aria-label={t("onboarding.resume.inProgress", { percent })} />
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 text-white hover:from-pink-600 hover:to-rose-600"
          onClick={(e) => {
            e.stopPropagation();
            navigate(USER_ROUTES.onboarding(locale, eventUuid, step));
          }}
          data-testid="onboarding-resume-continue"
        >
          {t("onboarding.resume.continue")}
          <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            navigate(USER_ROUTES.event(locale, eventUuid));
          }}
          data-testid="onboarding-resume-editor"
        >
          <PencilLine className="mr-2 h-4 w-4" aria-hidden="true" />
          {t("onboarding.resume.openEditor")}
        </Button>
      </div>
    </div>
  );
}
